import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import {
    Suspense,
    createContext,
    Fragment,
    useContext,
    useEffect,
    useMemo,
    useState,
    useCallback,
} from "react";

import solanaIcon from "../assets/Solana Icon.svg";
import polygonIcon from "../assets/Polygon Icon.svg";

import { enableMapSet } from "immer";
import { useImmer } from "use-immer";
import { Dialog, Transition } from "@headlessui/react";
import { XCircleIcon } from "@heroicons/react/20/solid";
import { XMarkIcon } from "@heroicons/react/24/solid";

import { PolygonWalletModalProvider } from "../Providers/PolygonWalletModalProvider";
import { SolanaTransferModalProvider } from "../Providers/SolanaTransferModalProvider";
import { Spinner } from "./Spinner";
import { Toastify } from "./Toastify";
import { useNFTsDBTable, useUserNFTAddrs } from "../Utils/hooks";
import { useMetamaskWallet } from "../Contexts/useMetamaskWallet";
import { useSolanaTransferModal } from "../Contexts/useSolanaTransferModal";
import { useMetamaskWalletModal } from "../Contexts/useMetamaskWalletModal";
import { useAuth } from "../Contexts/useAuth";

const TOASTIFY_DELAY = 2000;
const MAX_SELECTED_TOASTIFY_MSG = "You can only select 10 items per swap.";
const MAX_SELECTED_NFTS = 10;
const NFTS_IMAGE_URL_PREFIX = process.env.REACT_APP_NFTS_IMAGE_URL_PREFIX;
const NewSwappingContext = createContext({
    nfts: [],
    stage: "",
    selectedNFTs: new Set(),
    sidebarOpen: false,
    setSidebarOpen: (open) => {},
    updateSelectedNFTs: (draft) => {},
    closeNewSwappingPage: () => {},
    setToastMsg: (msg) => {},
    openNewSwappingResultPage: () => {},
});
enableMapSet();

export function NewSwappingPage({
    closeNewSwappingPage,
    openNewSwappingResultPage,
}) {
    const { connection } = useConnection();
    const walletContext = useWallet();
    const {
        data: nftAddrs,
        error: metaplexErr,
        isLoading: retrieving,
        isValidating: refreshing,
    } = useUserNFTAddrs({ connection, wallet: walletContext });
    const { dbTable, error: dbErr, isLoading: loadingDB } = useNFTsDBTable();
    const [nfts, setNFTs] = useState([]);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [selectedNFTs, updateSelectedNFTs] = useImmer(new Set());
    const [msg, setToastMsg] = useState("");

    const stage = useMemo(() => {
        if (loadingDB) return "initdb";
        if (retrieving || refreshing) {
            return "retrieving";
        }
        if (metaplexErr || dbErr) {
            console.error(metaplexErr || dbErr);
            return "error";
        }
        if (nfts.length === 0) return "empty";
        return "loaded";
    }, [nfts, loadingDB, retrieving, refreshing, metaplexErr, dbErr]);

    useEffect(() => {
        if (loadingDB || retrieving || refreshing) return;
        let ignore = false;
        (async () => {
            if (ignore) return;
            try {
                const result = [];
                await dbTable
                    .where("token_address")
                    .anyOf(nftAddrs)
                    .each((r) => {
                        result.push(r);
                    });
                setNFTs(result);
                updateSelectedNFTs((draft) => {
                    draft.clear();
                });
            } catch (error) {
                console.log(error);
            }
        })();

        return () => {
            ignore = true;
        };
    }, [
        nftAddrs,
        dbTable,
        loadingDB,
        retrieving,
        refreshing,
        updateSelectedNFTs,
    ]);

    return (
        <NewSwappingContext.Provider
            value={{
                msg,
                nfts,
                stage,
                selectedNFTs,
                sidebarOpen,
                setSidebarOpen,
                updateSelectedNFTs,
                closeNewSwappingPage,
                setToastMsg,
                openNewSwappingResultPage,
            }}
        >
            <Layout />
            <Toastify
                content={msg}
                duration={TOASTIFY_DELAY}
                setToastMsg={setToastMsg}
            />
        </NewSwappingContext.Provider>
    );
}

function SideMenu() {
    const {
        nfts,
        selectedNFTs,
        updateSelectedNFTs,
        openNewSwappingResultPage,
    } = useContext(NewSwappingContext);
    const { currentVerifiedPolygonPubKey } = useMetamaskWallet();

    return (
        <div className="h-full overflow-y-auto scrollbar-hide">
            <div className="border-color-gray-200 border-b p-3 lg:p-6">
                <span className="text-lg font-bold text-gray-800">
                    Select for Swap
                </span>
            </div>
            <div className="border-b border-gray-100  p-3 lg:p-6">
                <div className="mb-5 flex justify-between">
                    <span className="text-base font-bold text-gray-800">
                        {selectedNFTs.size} of {MAX_SELECTED_NFTS} items
                    </span>
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            updateSelectedNFTs((draft) => {
                                draft.clear();
                            });
                        }}
                        className="text-base font-bold text-gray-800 disabled:cursor-not-allowed disabled:text-gray-300"
                        disabled={selectedNFTs.size === 0}
                    >
                        Clear All
                    </button>
                </div>
                <SelectedNFTsList />
            </div>
            {selectedNFTs.size > 0 && (
                <PolygonWalletModalProvider>
                    <MetamaskBox />
                </PolygonWalletModalProvider>
            )}
            {currentVerifiedPolygonPubKey !== "" && selectedNFTs.size > 0 && (
                <div className="p-3 lg:p-6">
                    <SolanaTransferModalProvider
                        selectedNFTs={[...selectedNFTs].map(
                            (indx) => nfts[indx]
                        )}
                        onTransferSuccess={openNewSwappingResultPage}
                    >
                        <ConfirmSwapBtn />
                    </SolanaTransferModalProvider>
                </div>
            )}
        </div>
    );
}

function ConfirmSwapBtn() {
    const { setVisible } = useSolanaTransferModal();
    const handleClick = useCallback(
        (e) => {
            e.preventDefault();
            setVisible(true);
        },
        [setVisible]
    );
    return (
        <button
            onClick={handleClick}
            className="inline-flex w-full items-center justify-center rounded-full border border-transparent bg-orange-600 px-5 py-2 font-burbank text-lg font-semibold text-white shadow-inner hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 lg:text-xl"
        >
            Swap
        </button>
    );
}

function MetamaskBox() {
    const { solanaPubKey } = useAuth();
    const { currentVerifiedPolygonPubKey } = useMetamaskWallet();
    const { setVisible } = useMetamaskWalletModal();
    const handleClick = useCallback(
        (e) => {
            e.preventDefault();
            setVisible(true);
        },
        [setVisible]
    );
    return (
        <div className="border-b border-gray-100  p-3 lg:p-6">
            <div className="mb-4">
                <span className="text-base font-bold text-gray-800">
                    Swap Details
                </span>
            </div>
            <div className="mb-4 flex items-center justify-between">
                <div className="flex w-1/2 flex-col justify-start">
                    <span className="text-sm font-bold text-gray-500">
                        From:
                    </span>
                    <p className="mt-2 inline-flex items-center">
                        <img
                            className="mr-2 h-6 w-6"
                            src={solanaIcon}
                            alt="Solana Icon"
                        />
                        <span
                            title={solanaPubKey}
                            className="text-sm font-medium text-gray-800 lg:text-base"
                        >
                            {`${solanaPubKey.slice(
                                0,
                                4
                            )}...${solanaPubKey.slice(-4)}`}
                        </span>
                    </p>
                </div>
                <div className="flex w-1/2 flex-col justify-start">
                    <span className="text-sm font-bold text-gray-500">To:</span>
                    <p className="mt-2 inline-flex items-center">
                        <img
                            className="mr-2 h-6 w-6"
                            src={polygonIcon}
                            alt="Polygon Icon"
                        />
                        {currentVerifiedPolygonPubKey === "" ? (
                            <span className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium text-gray-400 lg:text-base">
                                Not connected
                            </span>
                        ) : (
                            <span
                                title={currentVerifiedPolygonPubKey}
                                className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium text-gray-800 lg:text-base"
                            >{`${currentVerifiedPolygonPubKey.slice(
                                0,
                                6
                            )}...${currentVerifiedPolygonPubKey.slice(
                                -4
                            )}`}</span>
                        )}
                    </p>
                </div>
            </div>
            {currentVerifiedPolygonPubKey === "" && (
                <div>
                    <button
                        onClick={handleClick}
                        className="inline-flex w-full items-center justify-center rounded-full border border-transparent bg-orange-600 px-5 py-2 font-burbank text-lg font-semibold text-white shadow-inner hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 lg:text-xl"
                    >
                        Connect Polygon Wallet
                    </button>
                </div>
            )}
        </div>
    );
}

function SelectedNFTsList() {
    const { selectedNFTs, nfts, updateSelectedNFTs } =
        useContext(NewSwappingContext);
    const handleClick = useCallback(
        (indx) => {
            updateSelectedNFTs((draft) => {
                draft.delete(indx);
            });
        },
        [updateSelectedNFTs]
    );

    if (selectedNFTs.size === 0) {
        return (
            <div className="py-6">
                <span className="text-gray-500">
                    Select a Neocollectible to get started
                </span>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            {[...selectedNFTs].map((indx) => {
                const nft = nfts[indx];
                return (
                    <SelectedNFTCard
                        key={indx}
                        nft={nft}
                        indx={indx}
                        onClick={handleClick}
                    />
                );
            })}
        </div>
    );
}

function SelectedNFTCard({ nft, indx, onClick: handleClick }) {
    return (
        <div
            key={indx}
            className="flex items-center justify-start gap-2 lg:gap-4"
        >
            <div className="h-20 w-20 overflow-hidden rounded-md">
                <picture>
                    <source
                        type="image/avif"
                        srcSet={`${NFTS_IMAGE_URL_PREFIX}100x100/${nft.token_address}.avif 100w, ${NFTS_IMAGE_URL_PREFIX}200x200/${nft.token_address}.avif 200w`}
                        width="80"
                        height="80"
                    />
                    <source
                        type="image/webp"
                        srcSet={`${NFTS_IMAGE_URL_PREFIX}100x100/${nft.token_address}.webp 100w, ${NFTS_IMAGE_URL_PREFIX}200x200/${nft.token_address}.webp 200w`}
                        width="80"
                        height="80"
                    />
                    <img
                        draggable="false"
                        loading="lazy"
                        src={`${NFTS_IMAGE_URL_PREFIX}100x100/${nft.token_address}.png`}
                        srcSet={`${NFTS_IMAGE_URL_PREFIX}200x200/${nft.token_address}.png 200w`}
                        alt={nft.name}
                        className="h-full w-full object-cover"
                        width="80"
                        height="80"
                    />
                </picture>
            </div>
            <div className="flex h-min w-40 flex-col justify-around gap-0 lg:w-52">
                <span className="text-sm font-bold text-gray-800 lg:text-base">
                    {nft.token_name}
                </span>
            </div>
            <button
                className="hover:drop-shadow-lg"
                onClick={(e) => {
                    e.preventDefault();
                    handleClick(indx);
                }}
            >
                <svg
                    className="h-5 w-5 text-gray-600"
                    viewBox="0 0 20 21"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M19 4.99991H14V3.32991C13.9765 2.68973 13.7002 2.08497 13.2316 1.64821C12.7629 1.21144 12.1402 0.978307 11.5 0.999914H8.5C7.85975 0.978307 7.23706 1.21144 6.76843 1.64821C6.2998 2.08497 6.02346 2.68973 6 3.32991V4.99991H1C0.734784 4.99991 0.48043 5.10527 0.292893 5.29281C0.105357 5.48034 0 5.7347 0 5.99991C0 6.26513 0.105357 6.51948 0.292893 6.70702C0.48043 6.89456 0.734784 6.99991 1 6.99991H2V17.9999C2 18.7956 2.31607 19.5586 2.87868 20.1212C3.44129 20.6838 4.20435 20.9999 5 20.9999H15C15.7956 20.9999 16.5587 20.6838 17.1213 20.1212C17.6839 19.5586 18 18.7956 18 17.9999V6.99991H19C19.2652 6.99991 19.5196 6.89456 19.7071 6.70702C19.8946 6.51948 20 6.26513 20 5.99991C20 5.7347 19.8946 5.48034 19.7071 5.29281C19.5196 5.10527 19.2652 4.99991 19 4.99991ZM8 3.32991C8 3.16991 8.21 2.99991 8.5 2.99991H11.5C11.79 2.99991 12 3.16991 12 3.32991V4.99991H8V3.32991ZM16 17.9999C16 18.2651 15.8946 18.5195 15.7071 18.707C15.5196 18.8946 15.2652 18.9999 15 18.9999H5C4.73478 18.9999 4.48043 18.8946 4.29289 18.707C4.10536 18.5195 4 18.2651 4 17.9999V6.99991H16V17.9999Z"
                        fill="#5A5252"
                    />
                </svg>

                <span className="sr-only">Delete</span>
            </button>
        </div>
    );
}

function Main() {
    const { nfts, stage } = useContext(NewSwappingContext);
    return (
        <main className="grid grid-rows-nfts-wrapper py-8 px-4 sm:px-8 lg:px-20">
            <div className="mx-auto w-full max-w-7xl">
                <span className="text-lg font-bold text-gray-800">
                    {stage === "loaded" || stage === "empty"
                        ? nfts.length
                        : "-"}{" "}
                    Items
                </span>
            </div>
            <div className="mx-auto max-h-full w-full max-w-7xl overflow-y-auto overscroll-contain py-6 scrollbar-hide">
                {stage !== "loaded" ? (
                    <InfoBox />
                ) : (
                    <Suspense fallback={<Rendering />}>
                        <NFTsList />
                    </Suspense>
                )}
            </div>
        </main>
    );
}

function NFTsList() {
    const { nfts, updateSelectedNFTs, setToastMsg } =
        useContext(NewSwappingContext);
    const handleClick = useCallback(
        (indx) => {
            updateSelectedNFTs((draft) => {
                if (draft.has(indx)) {
                    draft.delete(indx);
                } else {
                    if (draft.size >= MAX_SELECTED_NFTS) {
                        setToastMsg(MAX_SELECTED_TOASTIFY_MSG);
                        return;
                    }
                    draft.add(indx);
                }
            });
        },
        [updateSelectedNFTs, setToastMsg]
    );
    return (
        <div className="grid w-full grid-cols-1 content-between items-center justify-evenly justify-items-center gap-y-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 2.5xl:grid-cols-5">
            {nfts.map((nft, indx) => (
                <NFTCard
                    key={nft.token_address}
                    nft={nft}
                    indx={indx}
                    onClick={handleClick}
                />
            ))}
        </div>
    );
}

function NFTCard({ nft, indx, onClick: handleClick }) {
    const { selectedNFTs } = useContext(NewSwappingContext);

    return (
        <div
            className="flex cursor-pointer items-center justify-center"
            onClick={(e) => {
                e.preventDefault();
                handleClick(indx);
            }}
        >
            <div className="relative flex h-80 w-50 shrink-0 grow-0 flex-col overflow-hidden rounded-lg border border-gray-100 bg-white shadow-md">
                <div className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center overflow-hidden rounded bg-black bg-opacity-30 backdrop-blur-md">
                    {selectedNFTs.has(indx) && (
                        <svg
                            className="h-5 w-5 text-white"
                            width="18"
                            height="14"
                            viewBox="0 0 18 14"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M6.71738 13.4C6.57112 13.3995 6.42652 13.369 6.29257 13.3103C6.15861 13.2515 6.03816 13.1659 5.93871 13.0587L0.754709 7.544C0.560925 7.33749 0.457115 7.06245 0.466116 6.77939C0.475118 6.49634 0.596194 6.22845 0.802709 6.03467C1.00922 5.84088 1.28426 5.73707 1.56732 5.74608C1.85037 5.75508 2.11826 5.87615 2.31204 6.08267L6.70671 10.7653L15.6774 0.952002C15.7684 0.838639 15.8817 0.745084 16.0102 0.677083C16.1387 0.609082 16.2797 0.56807 16.4247 0.556567C16.5696 0.545063 16.7154 0.563311 16.853 0.61019C16.9906 0.657069 17.1172 0.731588 17.225 0.829171C17.3328 0.926754 17.4195 1.04534 17.4797 1.17765C17.54 1.30995 17.5726 1.45319 17.5755 1.59855C17.5784 1.74391 17.5516 1.88833 17.4966 2.02295C17.4417 2.15756 17.3598 2.27952 17.256 2.38134L7.50671 13.048C7.40819 13.1572 7.28817 13.2448 7.15417 13.3054C7.02016 13.366 6.87509 13.3982 6.72804 13.4H6.71738Z"
                                fill="white"
                            />
                        </svg>
                    )}
                </div>
                <picture>
                    <source
                        type="image/avif"
                        srcSet={`${NFTS_IMAGE_URL_PREFIX}200x200/${nft.token_address}.avif 200w, ${NFTS_IMAGE_URL_PREFIX}400x400/${nft.token_address}.avif 400w`}
                        width="200"
                        height="200"
                    />
                    <source
                        type="image/webp"
                        srcSet={`${NFTS_IMAGE_URL_PREFIX}200x200/${nft.token_address}.webp 200w, ${NFTS_IMAGE_URL_PREFIX}400x400/${nft.token_address}.webp 400w`}
                        width="200"
                        height="200"
                    />
                    <img
                        draggable="false"
                        loading="lazy"
                        className="h-50 w-50"
                        src={`${NFTS_IMAGE_URL_PREFIX}200x200/${nft.token_address}.png`}
                        srcSet={`${NFTS_IMAGE_URL_PREFIX}200x200/${nft.token_address}.png 200w, ${NFTS_IMAGE_URL_PREFIX}400x400/${nft.token_address}.png 400w`}
                        alt={nft.token_name}
                        width="200"
                        height="200"
                    />
                </picture>
                <p className="flex flex-col p-4">
                    <span className="text-base font-bold text-gray-800">
                        {nft.token_name}
                    </span>
                </p>
            </div>
        </div>
    );
}

function Layout() {
    const { closeNewSwappingPage, sidebarOpen, setSidebarOpen } =
        useContext(NewSwappingContext);

    return (
        <>
            <div className="grid flex-1 grid-rows-nfts-page lg:pr-102">
                <div className="sticky top-0 z-10 box-content flex h-20 px-4 py-1 sm:px-8 lg:px-20">
                    <div className="m-auto flex w-full max-w-7xl items-center justify-between">
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                closeNewSwappingPage();
                            }}
                            type="button"
                            className="inline-flex items-center justify-center rounded-full border border-gray-300 bg-white py-2 px-4 shadow hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        >
                            <svg
                                width="14"
                                height="12"
                                viewBox="0 0 14 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M12.8333 6.83317H2.94996L5.97496 10.4665C6.045 10.5508 6.09775 10.648 6.13021 10.7527C6.16267 10.8573 6.1742 10.9673 6.16414 11.0764C6.14383 11.2968 6.03681 11.5001 5.86663 11.6415C5.69645 11.783 5.47705 11.851 5.25669 11.8307C5.03633 11.8104 4.83307 11.7034 4.69163 11.5332L0.52496 6.53317C0.496926 6.4934 0.471859 6.45162 0.449959 6.40817C0.449959 6.36651 0.408293 6.34151 0.391626 6.29984C0.353854 6.20429 0.334077 6.10258 0.333293 5.99984C0.334077 5.8971 0.353854 5.79539 0.391626 5.69984C0.391626 5.65817 0.433292 5.63317 0.449959 5.59151C0.471859 5.54806 0.496926 5.50628 0.52496 5.46651L4.69163 0.466506C4.76998 0.372437 4.86809 0.296787 4.979 0.244936C5.0899 0.193085 5.21087 0.166307 5.33329 0.166505C5.528 0.166125 5.7167 0.233938 5.86663 0.358172C5.95101 0.42813 6.02076 0.514047 6.07189 0.611003C6.12301 0.707959 6.15451 0.814049 6.16457 0.923196C6.17464 1.03234 6.16307 1.1424 6.13053 1.24707C6.09799 1.35174 6.04513 1.44897 5.97496 1.53317L2.94996 5.16651H12.8333C13.0543 5.16651 13.2663 5.2543 13.4225 5.41058C13.5788 5.56686 13.6666 5.77883 13.6666 5.99984C13.6666 6.22085 13.5788 6.43281 13.4225 6.58909C13.2663 6.74538 13.0543 6.83317 12.8333 6.83317Z"
                                    fill="#2E2726"
                                />
                            </svg>

                            <span className="ml-2.5 font-burbank text-base font-semibold text-gray-800">
                                Back
                            </span>
                        </button>
                        <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-full bg-orange-600 py-2 px-4 shadow-inner hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-600 focus:ring-offset-2 lg:hidden"
                            onClick={() => setSidebarOpen(true)}
                        >
                            <span className="font-burbank text-base font-semibold text-white">
                                Continue
                            </span>
                        </button>
                    </div>
                </div>
                <Main />
            </div>
            <Transition.Root show={sidebarOpen} as={Fragment}>
                <Dialog
                    as="div"
                    className="relative z-40 lg:hidden"
                    onClose={setSidebarOpen}
                >
                    <Transition.Child
                        as={Fragment}
                        enter="transition-opacity ease-linear duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-40 flex flex-row-reverse">
                        <Transition.Child
                            as={Fragment}
                            enter="transition ease-in-out duration-300 transform"
                            enterFrom="translate-x-full"
                            enterTo="translate-x-0"
                            leave="transition ease-in-out duration-300 transform"
                            leaveFrom="translate-x-0"
                            leaveTo="translate-x-full"
                        >
                            <Dialog.Panel className="relative flex w-full max-w-xs flex-1 flex-col bg-white sm:max-w-sm">
                                <Transition.Child
                                    as={Fragment}
                                    enter="ease-in-out duration-300"
                                    enterFrom="opacity-0"
                                    enterTo="opacity-100"
                                    leave="ease-in-out duration-300"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                >
                                    <div className="absolute top-0 left-0 -ml-12 pt-2">
                                        <button
                                            type="button"
                                            className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none"
                                            onClick={() =>
                                                setSidebarOpen(false)
                                            }
                                        >
                                            <span className="sr-only">
                                                Close sidebar
                                            </span>
                                            <XMarkIcon
                                                className="h-6 w-6 text-white"
                                                aria-hidden="true"
                                            />
                                        </button>
                                    </div>
                                </Transition.Child>
                                <SideMenu />
                            </Dialog.Panel>
                        </Transition.Child>
                        <div className="w-14 flex-shrink-0" aria-hidden="true">
                            {/* Dummy element to force sidebar to shrink to fit close icon */}
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>

            {/* Static sidebar for desktop */}
            <div className="hidden lg:fixed lg:inset-y-0 lg:right-0 lg:flex lg:w-102 lg:flex-col lg:shadow-lg">
                {/* Sidebar component, swap this element with another sidebar if you like */}
                <div className="flex flex-grow flex-col overflow-y-auto border-r border-gray-200 bg-white">
                    <SideMenu />
                </div>
            </div>
        </>
    );
}

function InfoBox() {
    const { stage } = useContext(NewSwappingContext);
    switch (stage) {
        case "initdb":
            return (
                <div className="flex w-full items-center justify-start">
                    <Spinner color="text-orange-500" size="h-5 w-5" />
                    <span className="text-xs font-medium text-gray-500 sm:text-base">
                        Initializing Neocollectibles information database...
                    </span>
                </div>
            );
        case "retrieving":
            return (
                <div className="flex w-full items-center justify-start">
                    <Spinner color="text-orange-500" size="h-5 w-5" />
                    <span className="text-xs font-medium text-gray-500 sm:text-base">
                        Retrieving your Neocollectibles...
                    </span>
                </div>
            );
        case "empty":
            return (
                <div className="flex w-full items-center justify-start">
                    <span className="text-base font-medium text-gray-500">
                        Seems you don’t have any Neocollectible yet.
                    </span>
                </div>
            );
        case "error":
            return (
                <div className="flex w-full items-center justify-start">
                    <XCircleIcon
                        className="h-5 w-5 shrink-0 text-orange-600"
                        aria-hidden="true"
                    />
                    <span className="grow text-center text-base font-medium leading-5 text-orange-600 sm:grow-0">
                        Failed to retrieve Neocollectibles. Please{" "}
                        <a
                            className="font-bold text-orange-600 underline"
                            href="#error"
                            onClick={(e) => {
                                e.preventDefault();
                                window.location.reload();
                            }}
                        >
                            refresh page
                        </a>
                        .
                    </span>
                </div>
            );
        default:
            return <></>;
    }
}

function Rendering() {
    return (
        <div className="flex w-full items-center justify-start">
            <Spinner color="text-orange-500" size="h-5 w-5" />
            <span className="text-xs font-medium text-gray-500 sm:text-base">
                Rendering your Neocollectibles...
            </span>
        </div>
    );
}
